import React from 'react';
import { Select } from '@base-ui/react/select';
import { cn } from '../../lib/cn';

export interface LedgerSelectOption {
  value: string;
  label: string;
}

interface LedgerSelectProps {
  value: string;
  options: LedgerSelectOption[];
  onValueChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  /**
   * Ref to the trigger button — lets select-only dialogs hand LedgerDialog
   * an initialFocus target, since the trigger is otherwise unreachable.
   */
  triggerRef?: React.Ref<HTMLButtonElement>;
}

export const LedgerSelect: React.FC<LedgerSelectProps> = ({
  value,
  options,
  onValueChange,
  ariaLabel,
  className,
  disabled,
  triggerRef,
}) => {
  // Cold caches can seed a select with an id whose options haven't arrived
  // yet; Base UI would render the raw value string in that window. Resolve
  // the label ourselves and fall back to the placeholder option's label (the
  // `value: ''` entry, when the consumer provides one) or an em-dash.
  const labelOf = (current: unknown): string => {
    const match = options.find((option) => option.value === current);
    if (match) return match.label;
    const placeholder = options.find((option) => option.value === '');
    return placeholder ? placeholder.label : '—';
  };

  return (
    <Select.Root
      items={options}
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        if (typeof next === 'string') {
          onValueChange(next);
        }
      }}
    >
      <Select.Trigger
        ref={triggerRef}
        aria-label={ariaLabel}
        className={cn(
          'inline-flex cursor-pointer items-baseline gap-1 text-scale-1 text-ink hover:text-accent',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
          className,
        )}
      >
        <Select.Value>{labelOf}</Select.Value>
        <span aria-hidden className="text-ink-muted">▾</span>
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner sideOffset={4} className="z-50">
          <Select.Popup className="border border-ink bg-paper-raised py-1">
            {options.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className="cursor-pointer px-3 py-1 text-scale-1 text-ink data-[highlighted]:bg-accent-soft data-[selected]:font-medium"
              >
                <Select.ItemText>{option.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
};
